/**
 * MarkdownRenderer — Renders AI markdown responses as rich HTML
 *
 * Features:
 *   - GFM tables, bold, italic, strikethrough, lists, blockquotes
 *   - Code blocks with syntax highlighting styling
 *   - Inline chart rendering via ```chart fenced blocks
 *   - Tailwind-styled prose for consistent look
 */

import { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { ChartRenderer, type ChartData, isChartBlock, normalizeChartData } from './ChartRenderer';

// ─── Custom components for styled markdown ────────

const mdComponents: Components = {
  // Tables
  table: ({ children, ...props }) => (
    <div className="my-3 overflow-x-auto rounded-lg border">
      <table className="w-full text-xs" {...props}>{children}</table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead className="bg-muted/30 border-b" {...props}>{children}</thead>
  ),
  tbody: ({ children, ...props }) => (
    <tbody {...props}>{children}</tbody>
  ),
  tr: ({ children, ...props }) => (
    <tr className="border-b last:border-0 hover:bg-muted/10 transition-colors" {...props}>{children}</tr>
  ),
  th: ({ children, ...props }) => (
    <th className="px-3 py-1.5 text-left font-semibold text-muted-foreground whitespace-nowrap" {...props}>{children}</th>
  ),
  td: ({ children, ...props }) => (
    <td className="px-3 py-1.5 whitespace-nowrap" {...props}>{children}</td>
  ),

  // Code blocks — with chart detection
  code: ({ children, className, ...props }) => {
    const isInline = !className;
    const content = String(children).replace(/\n$/, '');

    // Detect ```chart blocks — flexible: accepts AI format variations
    if (className === 'language-chart') {
      try {
        const raw = JSON.parse(content);
        if (isChartBlock(raw)) {
          const chartData = normalizeChartData(raw as unknown as Record<string, unknown>);
          return <ChartRenderer data={chartData} />;
        }
      } catch { /* not valid chart JSON, render as code */ }
    }

    if (isInline) {
      return (
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-primary" {...props}>
          {children}
        </code>
      );
    }

    // Block code
    return (
      <div className="my-2 rounded-lg border bg-slate-950 dark:bg-slate-900 overflow-x-auto">
        <div className="flex items-center justify-between px-3 py-1 border-b border-slate-800">
          <span className="text-[10px] text-slate-400 font-mono">
            {className?.replace('language-', '') || 'code'}
          </span>
        </div>
        <pre className="px-3 py-2 text-xs font-mono text-slate-100 overflow-x-auto">
          <code className={className} {...props}>{children}</code>
        </pre>
      </div>
    );
  },

  // Pre — pass through (handled by code)
  pre: ({ children }) => <>{children}</>,

  // Headings
  h1: ({ children, ...props }) => <h1 className="text-lg font-bold mt-4 mb-2" {...props}>{children}</h1>,
  h2: ({ children, ...props }) => <h2 className="text-base font-bold mt-3 mb-1.5" {...props}>{children}</h2>,
  h3: ({ children, ...props }) => <h3 className="text-sm font-bold mt-2 mb-1" {...props}>{children}</h3>,

  // Paragraph
  p: ({ children, ...props }) => <p className="mb-2 last:mb-0 leading-relaxed" {...props}>{children}</p>,

  // Lists
  ul: ({ children, ...props }) => <ul className="list-disc list-inside mb-2 space-y-0.5" {...props}>{children}</ul>,
  ol: ({ children, ...props }) => <ol className="list-decimal list-inside mb-2 space-y-0.5" {...props}>{children}</ol>,
  li: ({ children, ...props }) => <li className="leading-relaxed" {...props}>{children}</li>,

  // Blockquote
  blockquote: ({ children, ...props }) => (
    <blockquote className="border-l-3 border-primary/40 pl-3 my-2 text-muted-foreground italic" {...props}>
      {children}
    </blockquote>
  ),

  // Bold / Italic
  strong: ({ children, ...props }) => <strong className="font-semibold" {...props}>{children}</strong>,
  em: ({ children, ...props }) => <em className="italic" {...props}>{children}</em>,

  // Horizontal rule
  hr: () => <hr className="my-3 border-t border-border" />,

  // Links
  a: ({ children, href, ...props }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
       className="text-primary underline underline-offset-2 hover:text-primary/80" {...props}>
      {children}
    </a>
  ),
};

// ─── Main Component ───────────────────────────────

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

function redactInternalIds(content: string): string {
  return content.replace(UUID_PATTERN, 'internal reference');
}

export const MarkdownRenderer = memo(function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const plugins = useMemo(() => [remarkGfm], []);
  const displayContent = useMemo(() => redactInternalIds(content), [content]);

  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={plugins} components={mdComponents}>
        {displayContent}
      </ReactMarkdown>
    </div>
  );
});
